<template>
  <div class="tw:grid tw:grid-cols-1 tw:items-start tw:gap-4 tw:lg:grid-cols-[13rem_minmax(0,1fr)]">
    <nav :aria-label="$t('Workflows')" class="tw:lg:sticky tw:lg:top-4">
      <button type="button"
              class="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border tw:border-zinc-300 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:lg:hidden"
              :aria-expanded="pickerOpen" aria-controls="marketing-workflow-list"
              @click="pickerOpen = !pickerOpen">
        <span class="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
          <span class="tw:truncate">{{ currentTab.name }}</span>
          <span class="tw:font-normal tw:text-zinc-400">{{ currentTab.count }}</span>
        </span>
        <ChevronDown class="tw:size-4 tw:shrink-0 tw:transition-transform" :class="{ 'tw:rotate-180': pickerOpen }" aria-hidden="true" />
      </button>

      <ul id="marketing-workflow-list"
          class="tw:m-0 tw:list-none tw:p-0 tw:max-lg:mt-1 tw:max-lg:rounded-lg tw:max-lg:border tw:max-lg:border-zinc-200 tw:max-lg:bg-white tw:max-lg:p-1 tw:lg:block"
          :class="pickerOpen ? 'tw:block' : 'tw:hidden'">
        <li v-for="tab in tabs" :key="tab.key"
            :class="{ 'tw:mt-1.5 tw:border-t tw:border-zinc-200 tw:pt-1.5': tab.key === OUTSIDE && workflows.length > 0 }">
          <button type="button" :aria-current="current === tab.key ? 'page' : undefined"
                  class="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:px-2.5 tw:py-1.5 tw:text-left tw:text-sm"
                  :class="current === tab.key
                    ? 'tw:bg-zinc-100 tw:font-medium tw:text-zinc-900'
                    : 'tw:text-zinc-600 tw:hover:bg-zinc-50 tw:hover:text-zinc-900'"
                  @click="select(tab.key)">
            <span class="tw:flex tw:min-w-0 tw:items-center tw:gap-1.5">
              <Inbox v-if="tab.key === OUTSIDE" class="tw:size-3.5 tw:shrink-0" aria-hidden="true" />
              <span class="tw:truncate">{{ tab.name }}</span>
            </span>
            <span class="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5 tw:text-xs tw:font-normal tw:text-zinc-400">
              <span v-if="tab.drafts > 0" class="tw:size-1.5 tw:rounded-full tw:bg-amber-500"
                    :title="$t('{n} draft | {n} drafts', tab.drafts)"></span>
              {{ tab.count }}
            </span>
          </button>
        </li>
      </ul>
    </nav>

    <div class="tw:min-w-0 tw:rounded-xl tw:border tw:border-zinc-200 tw:p-3 tw:md:p-4"
         style="background-color: #fafafa; background-image: radial-gradient(#d4d4d8 1px, transparent 1px); background-size: 16px 16px;">

      <template v-if="currentWorkflow">
        <div class="tw:mb-3 tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-x-3 tw:gap-y-2">
          <div class="tw:min-w-0">
            <h3 class="tw:m-0 tw:text-base tw:font-medium tw:max-lg:sr-only">{{ currentWorkflow.name }}</h3>
            <p v-if="currentWorkflow.description" class="tw:m-0 tw:text-sm tw:text-zinc-500">{{ currentWorkflow.description }}</p>
          </div>
          <div v-if="canEdit" class="tw:flex tw:shrink-0 tw:gap-1">
            <Button variant="outline" size="sm" @click="openDialog(currentWorkflow)">
              <Pencil aria-hidden="true" /> {{ $t('Edit') }}
            </Button>
            <ConfirmButton :title="$t('Delete the workflow')"
                           :message="$t('Its messages stay, outside any workflow. Nothing changes in what they send.')"
                           :action="updateUrl.replace('__workflow__', String(currentWorkflow.id))"
                           method="DELETE" mode="request" severity="danger"
                           trigger-variant="ghost" trigger-size="sm"
                           :confirm-label="$t('Delete')"
                           @success="reload(null)">
              <Trash2 class="tw:size-3.5" aria-hidden="true" /> {{ $t('Delete') }}
            </ConfirmButton>
          </div>
        </div>

        <template v-if="currentWorkflow.lanes.length">
          <div class="tw:flex tw:flex-col tw:gap-4.5">
            <WorkflowLane v-for="lane in currentWorkflow.lanes" :key="lane.event" :lane="lane"
                          :add-url="canEdit ? newMessageUrl(currentWorkflow, lane.event) : null" />
          </div>
          <a v-if="canEdit" :href="newMessageUrl(currentWorkflow)"
             class="tw:mt-4 tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:rounded-xl tw:border tw:border-dashed tw:border-zinc-400 tw:py-2.5 tw:text-sm tw:text-zinc-500 tw:no-underline tw:hover:border-zinc-600 tw:hover:text-zinc-900 tw:hover:no-underline">
            <Zap class="tw:size-4" aria-hidden="true" /> {{ $t('Add a trigger') }}
          </a>
        </template>
        <Empty v-else>
          <template #title>{{ $t('No message in this workflow yet') }}</template>
          <template #description>
            {{ $t('Pick the automatic messages of this use case, or create one and choose this workflow in its properties.') }}
          </template>
          <template #cta v-if="canEdit">
            <div class="tw:flex tw:flex-wrap tw:justify-center tw:gap-2">
              <Button @click="openDialog(currentWorkflow)">{{ $t('Pick messages') }}</Button>
              <Button variant="outline" as-child>
                <a :href="newMessageUrl(currentWorkflow)">{{ $t('Create a message') }}</a>
              </Button>
            </div>
          </template>
        </Empty>
      </template>

      <div v-else class="tw:grid tw:grid-cols-1 tw:items-start tw:gap-4 tw:md:grid-cols-3">
        <section v-for="group in outsideGroups" :key="group.key" class="tw:rounded-xl tw:bg-zinc-100 tw:px-3 tw:pt-2.5 tw:pb-3">
          <header class="tw:flex tw:justify-between tw:px-0.5 tw:pb-2 tw:text-xs tw:text-zinc-500">
            <span>{{ group.label }}</span>
            <span>{{ group.messages.length }}</span>
          </header>
          <div class="tw:flex tw:flex-col tw:gap-2.5">
            <MessageCard v-for="message in group.messages" :key="message.id" :message="message" />
            <p v-if="group.messages.length === 0" class="tw:m-0 tw:px-0.5 tw:text-xs tw:text-zinc-400">{{ $t('None') }}</p>
          </div>
        </section>
      </div>
    </div>

    <WorkflowDialog v-model:open="dialogOpen"
                    :workflow="editedWorkflow"
                    :workflows="workflows"
                    :automatic-messages="automaticMessages"
                    :templates="templates"
                    :store-url="storeUrl"
                    :update-url="updateUrl"
                    @saved="reload" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ChevronDown, Inbox, Pencil, Trash2, Zap } from '@lucide/vue'
import { Button } from '@/shadcn/components/ui/button'
import ConfirmButton from '@/components/ui/confirm/ConfirmButton.vue'
import Empty from '@/components/ui/Empty.vue'
import MessageCard from './MessageCard.vue'
import WorkflowDialog from './WorkflowDialog.vue'
import WorkflowLane from './WorkflowLane.vue'
import type { AutomaticMessageChoice, BoardOutside, BoardWorkflow, WorkflowTemplateChoice } from './types'

/**
 * The event's marketing messages by use case: the workflows listed aside, each drawn as a lane per
 * trigger, and last the messages no workflow holds. The workflow only arranges the messages, each
 * card opens the message itself. On small screens the list folds into a picker above the board.
 *
 * The page header creates workflows: it asks through the `marketing-workflow:create` window event.
 */
const props = withDefaults(defineProps<{
  workflows: BoardWorkflow[];
  outside: BoardOutside;
  automaticMessages: AutomaticMessageChoice[];
  templates?: WorkflowTemplateChoice[];
  storeUrl: string;
  /** Holds `__workflow__` in place of the workflow id. */
  updateUrl: string;
  createMessageUrl?: string;
  canEdit?: boolean;
}>(), {
  templates: () => [],
  createMessageUrl: '',
  canEdit: false,
})

const { t } = useI18n()
const OUTSIDE = 'outside'

const initialWorkflow = (): string => {
  const requested = new URLSearchParams(window.location.search).get('workflow')
  if (requested && props.workflows.some((workflow) => String(workflow.id) === requested)) return requested

  return props.workflows[0] ? String(props.workflows[0].id) : OUTSIDE
}

const current = ref<string>(initialWorkflow())

const pickerOpen = ref(false)

const select = (key: string): void => {
  current.value = key
  pickerOpen.value = false
  const url = new URL(window.location.href)
  url.searchParams.set('workflow', key)
  window.history.replaceState(null, '', url)
}

const outsideMessages = computed(() => [...props.outside.automatic, ...props.outside.scheduled, ...props.outside.manual])

const tabs = computed(() => [
  ...props.workflows.map((workflow) => ({
    key: String(workflow.id), name: workflow.name, count: workflow.message_ids.length, drafts: workflow.draft_count,
  })),
  {
    key: OUTSIDE,
    name: t('Outside workflows'),
    count: outsideMessages.value.length,
    drafts: outsideMessages.value.filter((message) => message.status === 'draft').length,
  },
])

const currentTab = computed(() => tabs.value.find((tab) => tab.key === current.value) ?? tabs.value[tabs.value.length - 1])

const currentWorkflow = computed(() => props.workflows.find((workflow) => String(workflow.id) === current.value) ?? null)

const outsideGroups = computed(() => [
  { key: 'automatic', label: t('Automatic, no workflow'), messages: props.outside.automatic },
  { key: 'scheduled', label: t('Scheduled'), messages: props.outside.scheduled },
  { key: 'manual', label: t('Manual'), messages: props.outside.manual },
])

/** The message form, started automatic in the workflow, on the trigger of a lane when one is given. */
const newMessageUrl = (workflow: BoardWorkflow, event?: string): string => {
  const url = new URL(props.createMessageUrl, window.location.origin)
  url.searchParams.set('workflow', String(workflow.id))
  if (event) url.searchParams.set('event', event)

  return url.toString()
}

const dialogOpen = ref(false)
const editedWorkflow = ref<BoardWorkflow | null>(null)

const openDialog = (workflow: BoardWorkflow | null): void => {
  editedWorkflow.value = workflow
  dialogOpen.value = true
}

const openCreateDialog = (): void => {
  if (props.canEdit) openDialog(null)
}

onMounted(() => window.addEventListener('marketing-workflow:create', openCreateDialog))
onBeforeUnmount(() => window.removeEventListener('marketing-workflow:create', openCreateDialog))

/** The board is drawn on the server: reload it on the saved workflow, or on the first one. */
const reload = (id: number | null): void => {
  const url = new URL(window.location.href)
  if (id === null) {
    url.searchParams.delete('workflow')
  } else {
    url.searchParams.set('workflow', String(id))
  }
  window.location.assign(url)
}
</script>
